import { SupportError } from './errors';

const MAX_BUFFER_BYTES = 128 * 1024;
const DECODE_CHUNK_BYTES = 4096;

/** 累计正文快照；临时内容不能加入消息历史或推进已读游标。 */
export interface AnswerSnapshot {
  messageId: string;
  text: string;
  responding: boolean;
}

/** UTF-8 与 SSE 行均可跨任意网络分块；只消费已完整结束的 answer 事件。 */
export async function consumeAnswerStream(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
  onAnswer: (snapshot: AnswerSnapshot) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const encoder = new TextEncoder();
  let buffer = '';
  let event = '';
  let data: string[] = [];
  let dataBytes = 0;
  let eventBytes = 0;
  const checkSize = (pending: string): void => {
    if (dataBytes + eventBytes + encoder.encode(pending).byteLength > MAX_BUFFER_BYTES) {
      throw new SupportError({ code: 0, kind: 'protocol', message: '回复事件超过大小限制' });
    }
  };
  const onAbort = (): void => { void reader.cancel().catch(() => {}); };
  signal.addEventListener('abort', onAbort);
  const line = (value: string): void => {
    if (value === '') {
      if (event === 'error' && data.length) {
        const error: unknown = JSON.parse(data.join('\n'));
        if (error && typeof error === 'object' && 'code' in error && typeof error.code === 'number') {
          throw new SupportError({ code: error.code, message: '回复连接已中断' });
        }
      }
      if (event === 'answer' && data.length) {
        const parsed: unknown = JSON.parse(data.join('\n'));
        if (parsed && typeof parsed === 'object' &&
          'messageId' in parsed && typeof parsed.messageId === 'string' &&
          'text' in parsed && typeof parsed.text === 'string' &&
          'responding' in parsed && typeof parsed.responding === 'boolean' && !signal.aborted) {
          onAnswer({ messageId: parsed.messageId, text: parsed.text, responding: parsed.responding });
        }
      }
      event = '';
      data = [];
      dataBytes = 0;
      eventBytes = 0;
    } else if (!value.startsWith(':')) {
      const colon = value.indexOf(':');
      const key = colon < 0 ? value : value.slice(0, colon);
      const content = colon < 0 ? '' : value.slice(colon + 1).replace(/^ /u, '');
      if (key === 'event') {
        event = content;
        eventBytes = encoder.encode(content).byteLength;
      }
      if (key === 'data') {
        data.push(content);
        dataBytes += encoder.encode(content).byteLength + 1;
      }
      checkSize('');
    }
  };
  const consumeLines = (done: boolean): void => {
    let boundary: number;
    while ((boundary = buffer.search(/[\r\n]/u)) >= 0) {
      // CRLF 可以跨两个网络包，不要误把后半个 LF 当空行。
      if (!done && buffer[boundary] === '\r' && boundary === buffer.length - 1) break;
      const width = buffer[boundary] === '\r' && buffer[boundary + 1] === '\n' ? 2 : 1;
      const next = buffer.slice(0, boundary);
      checkSize(next);
      line(next);
      buffer = buffer.slice(boundary + width);
      if (signal.aborted) return;
    }
    checkSize(buffer);
  };
  try {
    if (signal.aborted) return;
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        consumeLines(true);
        return;
      }
      // 单次网络分块可能包含很多合法帧；小块解码避免误限整个响应或一次分配超大字符串。
      for (let offset = 0; offset < value.byteLength && !signal.aborted; offset += DECODE_CHUNK_BYTES) {
        buffer += decoder.decode(value.subarray(offset, offset + DECODE_CHUNK_BYTES), { stream: true });
        consumeLines(false);
      }
    }
  } finally {
    signal.removeEventListener('abort', onAbort);
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}
